import { useEffect, useState } from "react";
import { api, type Invoice, type InvoiceStatus } from "../lib/api";
import { StatusBadge } from "../components/StatusBadge";
import { formatPrice } from "../lib/format";
import { navigate } from "../lib/route";

interface Props {
  id: string;
}

const STATUS_COLORS: Record<InvoiceStatus, string> = {
  draft: "#6b7280",
  sent:  "#3b82f6",
  paid:  "#22c55e",
  void:  "#ef4444",
};

const STATUS_LABELS: Record<InvoiceStatus, string> = {
  draft: "Concept",
  sent:  "Verzonden",
  paid:  "Betaald",
  void:  "Vervallen",
};

function formatShortDate(iso: string | null): string {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  } catch {
    return iso;
  }
}

type DraftLine = { description: string; quantity: number; unit_cents: number };

export function InvoiceDetailPage({ id }: Props) {
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Edit mode
  const [editing, setEditing] = useState(false);
  const [editLines, setEditLines] = useState<DraftLine[]>([]);
  const [editNotes, setEditNotes] = useState("");
  const [editDue, setEditDue] = useState("");
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // Confirm states
  const [confirmSend, setConfirmSend] = useState(false);
  const [sendingInvoice, setSendingInvoice] = useState(false);
  const [confirmCredit, setConfirmCredit] = useState(false);
  const [creatingCredit, setCreatingCredit] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const inv = await api.getInvoice(id);
        setInvoice(inv);
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : "Factuur laden mislukt");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  function startEdit() {
    if (!invoice) return;
    setEditLines(invoice.Lines.map((l) => ({
      description: l.Description,
      quantity: l.Quantity,
      unit_cents: l.UnitCents,
    })));
    setEditNotes(invoice.Notes || "");
    setEditDue(invoice.DueDate ? invoice.DueDate.slice(0, 10) : "");
    setEditError(null);
    setEditing(true);
  }

  function cancelEdit() {
    setEditing(false);
    setEditError(null);
  }

  function updateLine(idx: number, patch: Partial<DraftLine>) {
    setEditLines((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  }

  function addLine() {
    setEditLines((prev) => [...prev, { description: "", quantity: 1, unit_cents: 0 }]);
  }

  function removeLine(idx: number) {
    setEditLines((prev) => prev.filter((_, i) => i !== idx));
  }

  async function handleSave() {
    if (!invoice) return;
    setSaving(true);
    setEditError(null);
    try {
      const linesChanged =
        editLines.length !== invoice.Lines.length ||
        editLines.some((l, i) => {
          const o = invoice.Lines[i];
          return !o || l.description !== o.Description || l.quantity !== o.Quantity || l.unit_cents !== o.UnitCents;
        });
      let inv = await api.updateInvoice(invoice.ID, {
        notes: editNotes,
        due_date: editDue || undefined,
      });
      if (linesChanged) {
        inv = await api.replaceInvoiceLines(
          invoice.ID,
          editLines.map((l) => ({
            description: l.description.trim(),
            quantity: l.quantity,
            unit_cents: l.unit_cents,
          }))
        );
      }
      setInvoice(inv);
      setEditing(false);
    } catch (e: unknown) {
      setEditError(e instanceof Error ? e.message : "Opslaan mislukt");
    } finally {
      setSaving(false);
    }
  }

  async function handleMarkPaid() {
    if (!invoice) return;
    setActionError(null);
    try {
      const inv = await api.markInvoicePaid(invoice.ID);
      setInvoice(inv);
      setActionSuccess("Factuur gemarkeerd als betaald.");
    } catch (e: unknown) {
      setActionError(e instanceof Error ? e.message : "Actie mislukt");
    }
  }

  async function handleVoid() {
    if (!invoice) return;
    if (!confirm("Wil je deze factuur annuleren? Dit kan niet ongedaan worden gemaakt.")) return;
    setActionError(null);
    try {
      const inv = await api.voidInvoice(invoice.ID);
      setInvoice(inv);
    } catch (e: unknown) {
      setActionError(e instanceof Error ? e.message : "Actie mislukt");
    }
  }

  async function handleConfirmSend() {
    if (!invoice) return;
    setSendingInvoice(true);
    setActionError(null);
    try {
      const inv = await api.markInvoiceSent(invoice.ID);
      setInvoice(inv);
      setConfirmSend(false);
      setActionSuccess("Factuur gemarkeerd als verzonden.");
    } catch (e: unknown) {
      setActionError(e instanceof Error ? e.message : "Verzenden mislukt");
    } finally {
      setSendingInvoice(false);
    }
  }

  async function handleConfirmCredit() {
    if (!invoice) return;
    setCreatingCredit(true);
    setActionError(null);
    try {
      const newInvoice = await api.createCreditNote(invoice.ID);
      setConfirmCredit(false);
      navigate('/app/invoices/' + newInvoice.ID);
    } catch (e: unknown) {
      setActionError(e instanceof Error ? e.message : "Creditnota aanmaken mislukt");
    } finally {
      setCreatingCredit(false);
    }
  }

  if (loading) return <div className="page-container" aria-busy="true" />;
  if (error) return (
    <div className="page-container">
      <p style={{ color: "var(--danger)" }}>{error}</p>
    </div>
  );
  if (!invoice) return null;

  const isLocked = invoice.Status !== "draft";
  const editTotal = editLines.reduce((s, l) => s + l.quantity * l.unit_cents, 0);

  return (
    <div className="page-container">
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "1.5rem", flexWrap: "wrap" }}>
        <button
          className="btn-ghost"
          style={{ fontSize: "0.875rem", padding: "0.35rem 0.75rem" }}
          onClick={() => navigate('/app/invoices')}
        >
          ← Facturen
        </button>
        <h1 style={{ margin: 0, fontSize: "1.25rem", fontWeight: 700 }}>{invoice.Number}</h1>
        <StatusBadge
          label={STATUS_LABELS[invoice.Status]}
          color={STATUS_COLORS[invoice.Status]}
          style={{
            background: STATUS_COLORS[invoice.Status] + "22",
            color: STATUS_COLORS[invoice.Status],
            border: `1px solid ${STATUS_COLORS[invoice.Status]}55`,
          }}
        />
        {invoice.CreditOf && (
          <span style={{
            fontSize: "0.8rem",
            padding: "0.15rem 0.55rem",
            borderRadius: 999,
            background: "color-mix(in srgb, var(--warning) 15%, transparent)",
            color: "var(--warning)",
            border: "1px solid color-mix(in srgb, var(--warning) 40%, transparent)",
            fontWeight: 600,
          }}>
            Creditnota
          </span>
        )}
      </div>

      {/* Lock banner */}
      {isLocked && (
        <div style={{
          padding: "0.75rem 1rem",
          borderRadius: "8px",
          marginBottom: "1rem",
          background: "color-mix(in srgb, var(--warning) 10%, transparent)",
          border: "1px solid color-mix(in srgb, var(--warning) 30%, transparent)",
          color: "var(--text)",
          fontSize: "0.9rem",
        }}>
          Deze factuur is vergrendeld en kan niet meer worden bewerkt.
        </div>
      )}

      {/* Feedback */}
      {actionError && (
        <p style={{ color: "var(--danger)", marginBottom: "0.75rem" }}>{actionError}</p>
      )}
      {actionSuccess && (
        <p style={{ color: "var(--success)", marginBottom: "0.75rem" }}>{actionSuccess}</p>
      )}

      {/* Main card */}
      <div style={{
        background: "var(--surface)",
        border: "1px solid var(--border)",
        borderRadius: "14px",
        padding: "1.5rem",
        boxShadow: "var(--shadow)",
        display: "flex",
        flexDirection: "column",
        gap: "1.25rem",
      }}>

        {/* Metadata */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: "1rem" }}>
          <div>
            <div style={{ fontSize: "0.78rem", color: "var(--muted)", marginBottom: "0.2rem" }}>Factuurnummer</div>
            <div style={{ fontWeight: 600 }}>{invoice.Number}</div>
          </div>
          <div>
            <div style={{ fontSize: "0.78rem", color: "var(--muted)", marginBottom: "0.2rem" }}>Aangemaakt</div>
            <div>{formatShortDate(invoice.CreatedAt)}</div>
          </div>
          <div>
            <div style={{ fontSize: "0.78rem", color: "var(--muted)", marginBottom: "0.2rem" }}>Vervaldatum</div>
            <div>{formatShortDate(invoice.DueDate)}</div>
          </div>
          {invoice.PaidAt && (
            <div>
              <div style={{ fontSize: "0.78rem", color: "var(--muted)", marginBottom: "0.2rem" }}>Betaald op</div>
              <div>{formatShortDate(invoice.PaidAt)}</div>
            </div>
          )}
          {invoice.SentAt && (
            <div>
              <div style={{ fontSize: "0.78rem", color: "var(--muted)", marginBottom: "0.2rem" }}>Verzonden op</div>
              <div>{formatShortDate(invoice.SentAt)}</div>
            </div>
          )}
          {invoice.CreditOf && (
            <div>
              <div style={{ fontSize: "0.78rem", color: "var(--muted)", marginBottom: "0.2rem" }}>Creditnota van</div>
              <div style={{ fontSize: "0.875rem" }}>{invoice.CreditOf}</div>
            </div>
          )}
        </div>

        {/* Line items — view mode */}
        {!editing && (
          <>
            <div>
              <div style={{ fontSize: "0.78rem", color: "var(--muted)", marginBottom: "0.5rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>Regelposten</div>
              {invoice.Lines.length === 0 ? (
                <p style={{ color: "var(--muted)", fontSize: "0.875rem", margin: 0 }}>Geen regelposten.</p>
              ) : (
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.9rem" }}>
                  <thead>
                    <tr style={{ borderBottom: "1px solid var(--border)", color: "var(--muted)", fontSize: "0.8rem" }}>
                      <th style={{ textAlign: "left", padding: "0.4rem 0", fontWeight: 600 }}>Omschrijving</th>
                      <th style={{ textAlign: "right", padding: "0.4rem 0.5rem", fontWeight: 600 }}>Aantal</th>
                      <th style={{ textAlign: "right", padding: "0.4rem 0.5rem", fontWeight: 600 }}>Stukprijs</th>
                      <th style={{ textAlign: "right", padding: "0.4rem 0", fontWeight: 600 }}>Totaal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoice.Lines.map((l) => (
                      <tr key={l.ID} style={{ borderBottom: "1px solid var(--border)" }}>
                        <td style={{ padding: "0.5rem 0" }}>{l.Description}</td>
                        <td style={{ textAlign: "right", padding: "0.5rem 0.5rem" }}>{l.Quantity}</td>
                        <td style={{ textAlign: "right", padding: "0.5rem 0.5rem" }}>{formatPrice(l.UnitCents, invoice.Currency)}</td>
                        <td style={{ textAlign: "right", padding: "0.5rem 0", fontWeight: 500 }}>{formatPrice(l.TotalCents, invoice.Currency)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Totals */}
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "0.3rem", fontSize: "0.9rem" }}>
              {invoice.TaxCents > 0 && (
                <>
                  <div style={{ display: "flex", gap: "2rem" }}>
                    <span style={{ color: "var(--muted)" }}>Subtotaal</span>
                    <span>{formatPrice(invoice.SubtotalCents, invoice.Currency)}</span>
                  </div>
                  <div style={{ display: "flex", gap: "2rem" }}>
                    <span style={{ color: "var(--muted)" }}>BTW</span>
                    <span>{formatPrice(invoice.TaxCents, invoice.Currency)}</span>
                  </div>
                </>
              )}
              <div style={{ display: "flex", gap: "2rem", fontWeight: 700, fontSize: "1rem" }}>
                <span>Totaal</span>
                <span>{formatPrice(invoice.TotalCents, invoice.Currency)}</span>
              </div>
            </div>

            {/* Notes */}
            {invoice.Notes && (
              <div>
                <div style={{ fontSize: "0.78rem", color: "var(--muted)", marginBottom: "0.35rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>Notities</div>
                <p style={{ margin: 0, whiteSpace: "pre-wrap", fontSize: "0.9rem", color: "var(--text-secondary)" }}>{invoice.Notes}</p>
              </div>
            )}
          </>
        )}

        {/* Edit mode (draft only) */}
        {editing && (
          <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            <div>
              <div style={{ fontSize: "0.78rem", color: "var(--muted)", marginBottom: "0.5rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>Regelposten bewerken</div>
              <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
                {editLines.length === 0 && (
                  <p style={{ margin: 0, color: "var(--muted)", fontSize: "0.875rem" }}>Geen regelposten.</p>
                )}
                {editLines.map((l, idx) => (
                  <div key={idx} style={{ display: "grid", gridTemplateColumns: "minmax(160px, 3fr) minmax(60px, 1fr) minmax(100px, 1.4fr) auto", gap: "0.4rem", alignItems: "center" }}>
                    <input
                      placeholder="Omschrijving"
                      value={l.description}
                      onChange={(e) => updateLine(idx, { description: e.target.value })}
                    />
                    <input
                      type="number" min={1} step={1}
                      value={l.quantity}
                      onChange={(e) => updateLine(idx, { quantity: Math.max(1, parseInt(e.target.value, 10) || 1) })}
                      title="Aantal"
                    />
                    <input
                      type="number" min={0} step="0.01"
                      value={(l.unit_cents / 100).toString()}
                      onChange={(e) => {
                        const v = parseFloat(e.target.value);
                        updateLine(idx, { unit_cents: Math.max(0, Math.round((isNaN(v) ? 0 : v) * 100)) });
                      }}
                      title="Stukprijs"
                    />
                    <button
                      type="button"
                      className="btn-ghost"
                      style={{ color: "var(--danger)", padding: "0.3rem 0.55rem" }}
                      onClick={() => removeLine(idx)}
                      title="Verwijder regel"
                    >
                      ✕
                    </button>
                  </div>
                ))}
                <button type="button" className="btn-ghost" style={{ alignSelf: "flex-start", fontSize: "0.85rem" }} onClick={addLine}>
                  + Regel toevoegen
                </button>
                <small style={{ color: "var(--muted)" }}>
                  Subtotaal: {formatPrice(editTotal, invoice.Currency)}
                </small>
              </div>
            </div>

            <div className="form-group" style={{ marginBottom: 0 }}>
              <label style={{ display: "block", marginBottom: "0.35rem", fontSize: "0.875rem", color: "var(--muted)" }}>Vervaldatum</label>
              <input
                type="date"
                value={editDue}
                onChange={(e) => setEditDue(e.target.value)}
                style={{ maxWidth: "220px" }}
              />
            </div>

            <div className="form-group" style={{ marginBottom: 0 }}>
              <label style={{ display: "block", marginBottom: "0.35rem", fontSize: "0.875rem", color: "var(--muted)" }}>Notities</label>
              <textarea
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                rows={3}
              />
            </div>

            {editError && <p style={{ color: "var(--danger)", margin: 0, fontSize: "0.875rem" }}>{editError}</p>}

            <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
              <button className="btn-ghost" onClick={cancelEdit} disabled={saving}>Annuleren</button>
              <button className="btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? "Opslaan…" : "Opslaan"}
              </button>
            </div>
          </div>
        )}

        {/* "Verzenden" inline confirm */}
        {confirmSend && (
          <div style={{
            background: "color-mix(in srgb, var(--warning) 10%, transparent)",
            border: "1px solid color-mix(in srgb, var(--warning) 40%, transparent)",
            borderRadius: "8px",
            padding: "1rem",
          }}>
            <p style={{ margin: "0 0 0.75rem", fontSize: "0.9rem" }}>
              <strong>Let op:</strong> Na het verzenden wordt de factuur vergrendeld en kan deze niet meer worden bewerkt. Doorgaan?
            </p>
            <div style={{ display: "flex", gap: "0.5rem" }}>
              <button className="btn-ghost" style={{ fontSize: "0.875rem" }} onClick={() => setConfirmSend(false)} disabled={sendingInvoice}>Annuleren</button>
              <button className="btn-primary" style={{ fontSize: "0.875rem" }} onClick={handleConfirmSend} disabled={sendingInvoice}>
                {sendingInvoice ? "Bezig…" : "Bevestigen"}
              </button>
            </div>
          </div>
        )}

        {/* "Creditnota" inline confirm */}
        {confirmCredit && (
          <div style={{
            background: "color-mix(in srgb, var(--info) 10%, transparent)",
            border: "1px solid color-mix(in srgb, var(--info) 40%, transparent)",
            borderRadius: "8px",
            padding: "1rem",
          }}>
            <p style={{ margin: "0 0 0.75rem", fontSize: "0.9rem" }}>
              Er wordt een nieuwe creditnota aangemaakt op basis van deze factuur. Je wordt doorgestuurd naar de nieuwe creditnota. Doorgaan?
            </p>
            <div style={{ display: "flex", gap: "0.5rem" }}>
              <button className="btn-ghost" style={{ fontSize: "0.875rem" }} onClick={() => setConfirmCredit(false)} disabled={creatingCredit}>Annuleren</button>
              <button className="btn-primary" style={{ fontSize: "0.875rem" }} onClick={handleConfirmCredit} disabled={creatingCredit}>
                {creatingCredit ? "Bezig…" : "Bevestigen"}
              </button>
            </div>
          </div>
        )}

        {/* Action buttons */}
        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", paddingTop: "0.25rem", borderTop: "1px solid var(--border)" }}>
          {invoice.Status === "draft" && !editing && (
            <button className="btn-ghost" style={{ fontSize: "0.875rem" }} onClick={startEdit}>
              Bewerken
            </button>
          )}
          {invoice.Status === "draft" && (
            <button
              className="btn-ghost"
              style={{ fontSize: "0.875rem" }}
              onClick={() => { setConfirmSend(true); setConfirmCredit(false); setActionError(null); setActionSuccess(null); }}
            >
              Verzenden
            </button>
          )}
          {invoice.Status === "draft" && (
            <button className="btn-ghost" style={{ fontSize: "0.875rem", color: "var(--danger)" }} onClick={handleVoid}>
              Annuleren
            </button>
          )}
          {invoice.Status === "sent" && (
            <button className="btn-ghost" style={{ fontSize: "0.875rem" }} onClick={handleMarkPaid}>
              Markeer betaald
            </button>
          )}
          {(invoice.Status === "sent" || invoice.Status === "paid") && (
            <button
              className="btn-ghost"
              style={{ fontSize: "0.875rem" }}
              onClick={() => { setConfirmCredit(true); setConfirmSend(false); setActionError(null); setActionSuccess(null); }}
            >
              Creditnota aanmaken
            </button>
          )}
          {invoice.Status !== "draft" && (
            <button
              className="btn-ghost"
              style={{ fontSize: "0.875rem" }}
              onClick={() => window.open(api.getInvoicePrintUrl(invoice.ID), "_blank")}
            >
              Afdrukken / PDF
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
